import { ResizeObserver } from "@juggle/resize-observer";
import classNames from "classnames";
import React from "react";
import Modal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { useMountEffect } from "../../../hooks/useMountEffect";
import { WebPageURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import * as signals from "../../../store/signals";
import { CSR_TOOLBAR_HEIGHT_VAR } from "../../../styles/csr-toolbar";
import { navigateToURL } from "../../../utils/urls";
import { BasketSearch } from "../BasketSearch";
import { CustomerCreate } from "../CustomerCreate";
import { CustomerDetails } from "../CustomerDetails";
import { CustomerSearch } from "../CustomerSearch";
import { Vouchers } from "../Vouchers";
import { ICustomerSummary } from "../models";
import { viewStackClear, viewStackPop, viewStackSetRoot } from "../reducers";
import { IReduxState } from "../reducers.interfaces";
import {
    loadCountries,
    loadCurrentlyAssistedBasket,
    loadCurrentlyAssistedCustomer,
} from "../thunks";

// Load Toolbar styles
import "./index.scss";

Modal.defaultStyles = {
    overlay: {},
    content: {},
};

const PANELS = [
    {
        linkName: t`Customers`,
        component: CustomerSearch,
    },
    {
        linkName: t`New Customer`,
        component: CustomerCreate,
    },
    {
        linkName: t`Baskets`,
        component: BasketSearch,
    },
    {
        linkName: t`Vouchers`,
        component: Vouchers,
    },
];

const getCustomerName = (customer: ICustomerSummary): string => {
    if (customer.first_name && customer.last_name) {
        return `${customer.first_name} ${customer.last_name}`;
    }
    if (customer.email) {
        return customer.email;
    }
    return t`Anonymous`;
};

const renderCurrentlyAssistedUser = (
    customer: ICustomerSummary | null,
    dispatch: ReturnType<typeof useAppDispatch>,
): React.ReactNode => {
    if (!customer) {
        return null;
    }
    const name = getCustomerName(customer);
    const openDetails = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        const component = <CustomerDetails url={customer.url} />;
        dispatch(viewStackSetRoot({ name: t`Customer Details`, component }));
    };
    return (
        <li
            key="assisting-user"
            className="csr-toolbar__item"
            onClick={openDetails}
        >
            {t`Currently Assisting: ${name}`}
        </li>
    );
};

const renderCurrentlyAssistedBasket = (
    basket: IReduxState["assisting"]["basket"],
    onGotoBasket: () => void,
): React.ReactNode => {
    if (!basket) {
        return null;
    }
    return (
        <li
            key="assisting-basket"
            className="csr-toolbar__item"
            onClick={onGotoBasket}
        >
            {t`Basket: ${basket.encoded_basket_id}`}
        </li>
    );
};

const renderPanelLinks = (
    activePanel: number | null,
    onOpenModal: (panelIndex: number) => void,
    currentlyAssistedUser: React.ReactNode,
    currentlyAssistedBasket: React.ReactNode,
): React.ReactNode[] => {
    const elems: React.ReactNode[] = PANELS.map((def, index) => {
        const classes = classNames({
            "csr-toolbar__item": true,
            "csr-toolbar__item--active": activePanel === index,
        });
        return (
            <li
                key={index}
                className={classes}
                onClick={() => onOpenModal(index)}
            >
                {def.linkName}
            </li>
        );
    });
    if (currentlyAssistedUser) {
        elems.push(currentlyAssistedUser);
    }
    if (currentlyAssistedBasket) {
        elems.push(currentlyAssistedBasket);
    }
    return elems;
};

const renderActivePanel = (
    views: IReduxState["views"],
    dispatch: ReturnType<typeof useAppDispatch>,
    onCloseModal: (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => void,
): React.ReactNode => {
    const stack = views.stack;
    const activeIndex = stack.length - 1;
    if (activeIndex <= -1) {
        return null;
    }
    const breadcrumbs = stack
        .map((v) => {
            return v.name;
        })
        .join(" > ");
    const view = stack[activeIndex];
    const content = React.cloneElement(view.component);
    return (
        <div className="csr-modal">
            <div className="csr-modal__back">
                <button
                    disabled={activeIndex === 0}
                    onClick={() => {
                        dispatch(viewStackPop());
                    }}
                >
                    {t`Back`}
                </button>
                <span>{breadcrumbs}</span>
            </div>
            <div className="csr-modal__close">
                <button onClick={onCloseModal}>{t`Close`}</button>
            </div>
            <div className="csr-modal__title">
                <h1>{view.name}</h1>
            </div>
            {content}
        </div>
    );
};

export const Toolbar = (props: {
    dashboard: WebPageURL;
    basket: WebPageURL;
}): React.ReactNode => {
    const [activePanel, setActivePanel] = React.useState<number | null>(null);
    const dispatch = useAppDispatch();
    const assisting = useAppSelector((state) => state.csr.assisting);
    const views = useAppSelector((state) => state.csr.views);
    const toolbarRef = React.useRef<HTMLDivElement | null>(null);

    React.useLayoutEffect(() => {
        const toolbar = toolbarRef.current;
        if (!toolbar) {
            return;
        }
        const root = toolbar.ownerDocument.documentElement;

        const publishHeight = () => {
            root.style.setProperty(
                CSR_TOOLBAR_HEIGHT_VAR,
                `${toolbar.offsetHeight}px`,
            );
        };

        publishHeight();

        // offsetHeight (border-box), not entries[].contentRect (content-box, under-reports the 1px bottom border).
        const resizeObserver = new ResizeObserver(publishHeight);
        resizeObserver.observe(toolbar);

        return () => {
            resizeObserver.disconnect();
            root.style.removeProperty(CSR_TOOLBAR_HEIGHT_VAR);
        };
    }, []);

    const onOpenModal = (
        panelIndex: number,
        event?: React.MouseEvent<HTMLElement>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        const def = PANELS[panelIndex];
        const Panel = def.component;
        const component = <Panel />;
        dispatch(viewStackSetRoot({ name: def.linkName, component }));
        setActivePanel(panelIndex);
    };

    const onCloseModal = (
        event:
            | React.MouseEvent<Element>
            | React.KeyboardEvent<Element>
            | MouseEvent
            | KeyboardEvent,
    ) => {
        event.preventDefault();
        dispatch(viewStackClear());
        setActivePanel(null);
    };

    const onGotoDashboard = () => {
        navigateToURL(props.dashboard);
    };

    const onGotoBasket = () => {
        navigateToURL(props.basket);
    };

    const onAfterStartNewBasket = () => {
        const window = getWindow();
        if (window) {
            window.location.reload();
        }
    };

    useMountEffect(() => {
        signals.csr.onAfterStartNewBasket.on(onAfterStartNewBasket);
        void dispatch(loadCountries());
        void dispatch(loadCurrentlyAssistedCustomer());
        void dispatch(loadCurrentlyAssistedBasket());

        return () => {
            signals.csr.onAfterStartNewBasket.off(onAfterStartNewBasket);
        };
    });

    const currentlyAssistedUser = renderCurrentlyAssistedUser(
        assisting.customer,
        dispatch,
    );
    const currentlyAssistedBasket = renderCurrentlyAssistedBasket(
        assisting.basket,
        onGotoBasket,
    );
    const panelLinks = renderPanelLinks(
        activePanel,
        onOpenModal,
        currentlyAssistedUser,
        currentlyAssistedBasket,
    );
    const activePanelContent = renderActivePanel(views, dispatch, onCloseModal);

    return (
        <div ref={toolbarRef} className="csr-toolbar sticky">
            <div className="csr-toolbar__inner">
                <ul className="csr-toolbar__items">
                    <li className="csr-toolbar__item csr-toolbar__item--title">
                        {t`CSR Tools`}
                    </li>
                    <li className="csr-toolbar__item" onClick={onGotoDashboard}>
                        {t`Dashboard`}
                    </li>
                    {panelLinks}
                </ul>
            </div>
            <Modal
                isOpen={views.stack.length > 0}
                onRequestClose={onCloseModal}
                contentLabel={t`CSR Toolbar`}
                style={{
                    overlay: {
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.75)",
                        zIndex: 9010,
                    },
                    content: {
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        border: "1px solid #CCC",
                        background: "#FFF",
                        overflow: "auto",
                        WebkitOverflowScrolling: "touch",
                        borderRadius: "4px",
                        outline: "none",
                        padding: "0",
                        minWidth: "600px",
                        maxWidth: "1200px",
                        width: "90%",
                        height: "90%",
                    },
                }}
            >
                {activePanelContent}
            </Modal>
        </div>
    );
};
